import {
  createFileRoute,
  useParams,
  Outlet,
  useLocation,
  useRouter,
} from "@tanstack/react-router"
import { checkPermissions } from "@/utils/checkPermissions"
import {
  LayoutWithSidebar,
  DetailSidebar,
  useLayoutConfig,
  ProfileCard,
  DetailInfoList,
  Separator,
  CollapsibleWithTitle,
  Slider,
  formatBool,
  formatCurrencyAmount,
  formatLearningPathName,
  studentTypeMap,
  formatDateToDutch,
  Badge,
  getStudentStatusLabel,
  studentStatusToBadgeVariant,
  InterruptionCard,
} from "toptaal-shared"
import ScreenWrapper from "@/components/ScreenWrapper"
import { useStudentSuspenseQuery } from "@/api/queries/students/useStudentSuspenseQuery"
import { useStudentCustomerManagersSuspenseQuery } from "@/api/queries/students/useStudentCustomerManagersSuspenseQuery"
import { StudentCustomerManagers } from "@/pages/cursisten/$studentId/StudentCustomerManagers"
import { useStudentGroupsQuery } from "@/api/queries/students/useStudentGroupsQuery"
import { useStudentCurrentInterruptionQuery } from "@/api/queries/students/useStudentCurrentInterruptionQuery"

export const Route = createFileRoute("/beheer/cursisten/$studentId")({
  component: Parent,
  beforeLoad: () => checkPermissions("read", "students"),
})

function Parent() {
  const { pathname } = useLocation()
  const layoutConfig = useLayoutConfig()

  const { studentId } = useParams({
    from: "/beheer/cursisten/$studentId",
  })

  const base = `/beheer/cursisten/${studentId}`

  const sidebarNavigationItems = [
    {
      label: "Gegevens",
      to: `${base}/gegevens`,
    },
    {
      label: "Beschikbaarheid",
      to: `${base}/beschikbaarheid`,
    },
    {
      label: "Intake",
      to: `${base}/intake`,
    },
    {
      label: "2e aanspreekpunt",
      to: `${base}/tweede-aanspreekpunt`,
    },
    {
      label: "Examenresultaten",
      to: `${base}/examenresultaten`,
    },
    {
      label: "Voortgang",
      to: `${base}/voortgang`,
    },
    {
      label: "Correspondentie",
      to: `${base}/correspondentie`,
    },
    {
      label: "Interne Notities",
      to: `${base}/notities`,
    },
    {
      label: "Rapportages",
      to: `${base}/rapportages`,
    },
    {
      label: "Laptops",
      to: `${base}/laptops`,
    },
    {
      label: "Lessen",
      to: `${base}/lessen`,
    },
    {
      label: "Documenten",
      to: `${base}/documenten`,
    },
  ]

  return (
    <ScreenWrapper className="!px-0">
      <LayoutWithSidebar
        rightPanelClassName={layoutConfig.rightPanelClassName}
        disableRightPanelSuspense={layoutConfig.disableRightPanelSuspense}
        sidebar={
          <DetailSidebar
            items={sidebarNavigationItems}
            isItemActive={(route: string) => pathname.startsWith(route)}
            goBackLabel="Cursist"
            goBackRoute="/beheer/cursisten"
          >
            <StudentDetails />
          </DetailSidebar>
        }
      >
        <Outlet />
      </LayoutWithSidebar>
    </ScreenWrapper>
  )
}

function StudentDetails() {
  const router = useRouter()
  const { studentId } = useParams({
    from: "/beheer/cursisten/$studentId",
  })
  const {
    data: { data: student },
  } = useStudentSuspenseQuery(Number(studentId))

  const {
    data: { customerManagers },
  } = useStudentCustomerManagersSuspenseQuery(Number(studentId))

  const loanSpaceDuo = student.loanSpaceDuo ?? 0
  // TODO: replace with actual used loan amount when that data is available from the API
  const mockedUsedLoanAmount = Math.round(loanSpaceDuo * 0.35)
  const status = student.currentStudentProjectOrganization?.status

  const { data: groupsData } = useStudentGroupsQuery({
    studentId: Number(studentId),
  })

  const { data: currentInterruption } = useStudentCurrentInterruptionQuery(
    student.currentStudentProjectOrganization?.id,
  )

  const handleNavigateToGroup = (groupId: number) => {
    router.navigate({
      to: "/beheer/groepen/$groupId/gegevens",
      params: { groupId: String(groupId) },
      search: { from: router.state.location.href },
    })
  }

  return (
    <>
      {currentInterruption && (
        <InterruptionCard interruption={currentInterruption} />
      )}

      <ProfileCard
        className="my-6"
        firstName={student.firstName}
        lastName={student.lastName}
        email={student.email}
        phoneNumber={student.phoneNumberCall}
      >
        <Separator className="my-4" />

        <DetailInfoList
          items={[
            {
              label: "Contract",
              value: student.currentStudentProjectOrganization?.project.name,
            },
            {
              label: "Leerlijn",
              value: formatLearningPathName({
                learningPath: student.currentLearningPath,
                moduleCode: student.currentModule?.moduleCode,
                fallback: "Leerlijn nog niet bekend",
              }),
            },
            {
              label: "App",
              value: formatBool(student.appInstalled),
            },

            {
              label: "Status",
              value: (
                <Badge variant={studentStatusToBadgeVariant(status)}>
                  {getStudentStatusLabel(status)}
                </Badge>
              ),
            },
            {
              label: "Aanmelddatum",
              value: formatDateToDutch(
                student.currentStudentProjectOrganization?.registeredAt,
                {
                  format: "date",
                },
              ),
            },
            ...(student.currentStudentProjectOrganization?.studentType != null
              ? [
                  {
                    label: "Student type",
                    value:
                      studentTypeMap[
                        student.currentStudentProjectOrganization.studentType
                      ],
                  },
                ]
              : []),
            ...(groupsData
              ? groupsData.data.lessonGroups.map((group) => ({
                  label: "Groep",
                  value: (
                    <span className="flex flex-col">
                      <span
                        role="button"
                        onClick={() => handleNavigateToGroup(group.id)}
                        className="text-primary-green underline cursor-pointer hover:text-primary-dark-green w-fit"
                      >
                        {group.id}
                      </span>
                      <span>
                        Startdatum{" "}
                        {formatDateToDutch(group.lessonStartDate) ?? "-"}
                      </span>
                    </span>
                  ),
                }))
              : []),
          ]}
        />
      </ProfileCard>

      <Separator className="mb-6" />

      {student.loanSpaceDuo !== null && (
        <>
          <CollapsibleWithTitle
            title="DUO leenruimte"
            canOpen={student.loanSpaceDuo !== null}
          >
            <div className="bg-white px-4 py-6 rounded">
              <Slider
                containerName="pt-6"
                className="data-[disabled]:opacity-100 cursor-not-allowed"
                disabled
                max={loanSpaceDuo}
                value={[mockedUsedLoanAmount]}
                startLabel={formatCurrencyAmount(loanSpaceDuo)}
                endLabel={formatCurrencyAmount(0)}
                formatBoundaryLabel={formatCurrencyAmount}
                formatValueLabel={(value) =>
                  formatCurrencyAmount(loanSpaceDuo - value)
                }
              />
            </div>
          </CollapsibleWithTitle>

          <Separator className="my-6" />
        </>
      )}

      <CollapsibleWithTitle
        title="Contactpersoon"
        isOpenInitial={false}
        canOpen={customerManagers.length > 0}
      >
        <StudentCustomerManagers />
      </CollapsibleWithTitle>

      <Separator className="my-6" />
    </>
  )
}
